import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { Size } from '../types';

export type CanvasPainter = (
  context: CanvasRenderingContext2D,
  viewport: Size
) => void;

const EMPTY_SIZE: Size = { width: 0, height: 0 };

/** Tracks the size of the element the chart is mounted in. */
export const useContainerSize = (): {
  containerRef: (element: HTMLDivElement | null) => void;
  viewport: Size;
} => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [viewport, setViewport] = useState<Size>(EMPTY_SIZE);

  const containerRef = useCallback((element: HTMLDivElement | null) => {
    setContainer(element);
  }, []);

  useLayoutEffect(() => {
    if (!container) return;

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      const next = { width: Math.round(width), height: Math.round(height) };
      /* A value-equal size leaves the state alone. `contentRect` is fractional and this rounds, so
         a sub-pixel change during a divider drag describes a viewport already painted - and a
         fresh object reads as a change to the paint effect, which is the whole repaint. On the
         contour chart that is the per-device-pixel band fill. */
      setViewport(current =>
        current.width === next.width && current.height === next.height
          ? current
          : next
      );
    });
    observer.observe(container);

    return () => observer.disconnect();
  }, [container]);

  return { containerRef, viewport };
};

/**
 * Hosts a canvas sized to `viewport` and repaints when asked.
 *
 * The backing store is re-allocated at the device pixel ratio on every change or the whole plot
 * renders soft on a retina display - the one bug that makes hand-rolled canvas work look worse than
 * SVG for no reason.
 */
export const useChartCanvas = (
  paint: CanvasPainter,
  /**
   * Owned by the caller so the interaction hook can bind native listeners to the same element.
   * A canvas takes one ref, and both hooks need it.
   */
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  viewport: Size
): {
  /**
   * Whether a frame has painted, read at call time rather than rendered.
   *
   * An unpainted canvas is not a zero-sized one: the backing store is only assigned inside the
   * frame below, so until it runs the element carries the HTML default of 300x150 and reads as a
   * perfectly ordinary canvas. Anything asking "is there a picture here" - the export hook is the
   * one caller - has to ask this instead of measuring.
   */
  hasPainted: () => boolean;
} => {
  const painted = useRef(false);
  const [pixelRatio, setPixelRatio] = useState(() =>
    typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1
  );

  /**
   * Repaints when the display's density changes.
   *
   * `devicePixelRatio` is read inside the frame below and is not observable directly, and it
   * changes without the element resizing: dragging a window between a retina and a non-retina
   * display keeps the CSS box identical, so `ResizeObserver` never fires. Left unwatched, the
   * canvas keeps the backing store it was allocated on the previous display - soft at half the
   * pixels the screen can show, or paying four times the rasterise for pixels nobody sees.
   *
   * The contour chart is the case that decides this: it has no interaction at all, so nothing
   * short of a resize would ever correct it. The 3D charts self-correct on the first orbit, which
   * is why this is easy to miss.
   *
   * Re-armed each time, because the query is written against the ratio currently in force and
   * stops matching the moment it changes.
   *
   * The state exists to schedule the frame, not to carry the value: the paint reads
   * `devicePixelRatio` itself, and by the time it runs the two agree. Holding it here as well
   * would be a second copy nothing can tell apart from the first.
   */
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const query = window.matchMedia(`(resolution: ${pixelRatio}dppx)`);
    const onChange = () => setPixelRatio(window.devicePixelRatio || 1);

    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [pixelRatio]);

  /**
   * Paints at most once per animation frame.
   *
   * React's commit phase is the wrong scheduler for a canvas. Every pointer move during an
   * orbit dispatches, which re-renders, which changes the painter's identity and would repaint
   * synchronously - so three moves delivered in one frame meant sorting and filling several
   * thousand facets three times for a single displayed frame. Scheduling through
   * `requestAnimationFrame` and cancelling any frame still pending collapses a burst of
   * invalidations into the one paint the display can actually show.
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || viewport.width === 0 || viewport.height === 0) return;

    /* Declared before the request rather than initialised by it: the callback clears this handle,
       and a scheduler that runs it during the initialiser - which a test harness or a polyfill may
       do, though no browser does - reads the binding before it exists and throws. */
    let pendingFrame: number | undefined;

    pendingFrame = requestAnimationFrame(() => {
      pendingFrame = undefined;

      const ratio = window.devicePixelRatio || 1;
      const width = Math.round(viewport.width * ratio);
      const height = Math.round(viewport.height * ratio);

      /* Assigning `width` or `height` reallocates the backing store and clears the canvas, so
         it is guarded: during an orbit the size never changes, and reallocating a buffer at
         twice the device resolution on every frame is pure waste. */
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }

      /* Outside the guard, because the CSS size is not what the guard is protecting. A canvas
         with no `style.width` lays out at its intrinsic 300x150, and a device size that happens
         to equal 300x150 leaves the guard false - so at any ratio above 1 the chart drew
         magnified by the ratio and overflowed its container until something else resized it.
         The assignments are idempotent, so paying them every frame costs nothing. */
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;

      const context = canvas.getContext('2d');
      if (!context) return;

      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, viewport.width, viewport.height);
      paint(context, viewport);
      painted.current = true;
    });

    return () => {
      if (pendingFrame !== undefined) cancelAnimationFrame(pendingFrame);
    };
  }, [viewport, paint, canvasRef, pixelRatio]);

  /* Not cleared when the viewport goes to zero. A collapsed pane leaves the backing store holding
     the last frame, and that picture is a better answer for an export than none - what this guards
     is the canvas that has never held one. */
  const hasPainted = useCallback(() => painted.current, []);

  return { hasPainted };
};
